import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, HelpCircle, Phone } from "lucide-react";
import { canonicalUrl, absUrl, canonicalLink, geoMeta } from "@/lib/seo";
import ogImg from "@/assets/original/hero-shade.jpg";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const OG = absUrl(ogImg);

const FAQS: { q: string; a: string }[] = [
  {
    q: "How long does a custom awning last?",
    a: "As a general rule, awning frames last many years. Our aluminum framing is custom welded — aluminum doesn't rust like steel or pipe awnings. The biggest factor is sun exposure: north and east-facing awnings tend to last longer. Our welded aluminum frames are built to last 20+ years, and Sunbrella/Tempotest canvas typically holds color and integrity and carries a 10-year limited warranty.",
  },
  {
    q: "Does fabric color affect awning longevity?",
    a: "Yes. Darker colors like blue, black, and dark green tend to look better longer than red, yellow, or terra cotta. Some manufacturers like Glen Raven offer warranties on their products — check who your manufacturer is. And don't forget, our welded aluminum frames will outlast steel frames by far.",
  },
  {
    q: "Can you reuse awning frames with new fabric?",
    a: "Absolutely. Frames usually far outlast the canvas, so re-covering an existing frame with new Sunbrella canvas is a great way to refresh an awning at a fraction of the cost of full replacement.",
  },
  {
    q: "Are awnings waterproof?",
    a: "Awnings are water-repellent rather than fully waterproof. In most installations you'll see little to no leakage, but fabric type, awning pitch, mounting surface and storm severity can all affect performance during heavy rain.",
  },
  {
    q: "How long does it take to get a custom awning?",
    a: "All of our products are custom made. Lead time is typically about four weeks, but smaller jobs can be ready in as little as two weeks depending on the season.",
  },
  {
    q: "How much does a custom awning cost?",
    a: "Cost varies widely based on size, fabric, installation, and extras like graphics or lighting. Send us rough sizes for a ballpark, or we'll meet you on-site for an accurate bid — free of charge.",
  },
  {
    q: "Can you clean an awning?",
    a: "Yes. Both metal and fabric awnings can be cleaned with mild soap and water. For severe stains on fabric, vinegar or a diluted bleach solution may help — always check the manufacturer's recommended cleaning instructions first.",
  },
  {
    q: "What materials do you use?",
    a: "Our frames are welded aluminum (rust-proof and lifetime durable). Fabrics include Sunbrella, Tempotest, Serge Ferrari, and Phifertex — all premium marine-grade canvas and mesh built for Texas heat and UV.",
  },
  {
    q: "Do I need a permit to install an awning?",
    a: "Often yes — many cities and HOAs require a permit, and some have restrictions on size, color, or projection. We've worked with most local authorities across South & Central Texas and can help walk you through it.",
  },
  {
    q: "Will an awning save money on energy bills?",
    a: "Studies from the Professional Awning Manufacturers Association (PAMA) show fabric awnings can save homeowners up to $200/year on cooling costs by shading windows and reducing solar heat gain.",
  },
  {
    q: "Can you put a logo on an awning?",
    a: "Yes — we apply custom graphics, lettering, and logos using heat-applied vinyl or sewn appliqué for storefronts, restaurants, and branded commercial awnings.",
  },
  {
    q: "Do you service the whole state of Texas?",
    a: "We're based in Seguin and regularly install across San Antonio, New Braunfels, Austin, Houston, Corpus Christi, the Rio Grande Valley, and the Hill Country. Reach out — chances are we cover your area.",
  },
  {
    q: "Do you offer sail shades and tension structures?",
    a: "Yes. We engineer and install custom sail shades and tension shade structures with aircraft-grade hardware and HDPE shade fabric, sized and pitched for your specific site.",
  },
  {
    q: "Can you build pool, spa, and trailer covers too?",
    a: "Yes — pool and spa covers, BBQ pit covers, equipment covers, stock trailer tarps, and ag tarps are all part of what we do. If it needs custom canvas or welding, we can build it.",
  },
];

function FaqItem({ q, a, idx }: { q: string; a: string; idx: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-navy-deep/15">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-5 text-left transition hover:text-rust"
      >
        <span className="flex items-start gap-3 font-display text-lg font-semibold text-navy-deep md:text-xl">
          <span className="select-none font-mono text-sm text-rust/70">{String(idx + 1).padStart(2, "0")}</span>
          {q}
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-navy-deep transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="pb-6 pr-8 text-base leading-relaxed text-navy-deep/80">{a}</div>
      )}
    </div>
  );
}

function FaqsPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="bg-cream">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-5xl px-4 py-20 lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-rust">★ FREQUENTLY ASKED ★</div>
          <h1 className="mt-3 font-display text-4xl font-bold md:text-6xl">Awning & Shade FAQ</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">
            Real answers from 30+ years of building custom awnings, covers, and shade structures across Texas.
            Don't see your question? We'd love to hear it.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rust px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-rust/90"
            >
              <HelpCircle className="h-4 w-4" /> Ask a Question
            </Link>
            <a
              href="tel:+18303909588"
              className="inline-flex items-center gap-2 border-2 border-white/40 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> +1 (830) 390-9588
            </a>
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="mx-auto max-w-4xl px-4 py-16 lg:px-8">
        <div className="rounded-sm bg-white p-6 shadow-card md:p-10">
          {FAQS.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} idx={i} />
          ))}
        </div>

      </section>

      <LetsTalkForm />
      </main>
      <SiteFooter />
    </>
  );
}

export const Route = createFileRoute("/faqs")({
  head: () => ({
    links: [canonicalLink("/faqs")],
    meta: [
        ...geoMeta(),{ property: "og:url", content: canonicalUrl("/faqs") },
      { title: "Awning & Shade FAQ — Texas Made Shade & Covers" },
      {
        name: "description",
        content:
          "Answers on custom awnings, sail shades, covers, fabric, lifespan, pricing & lead times for San Antonio, Austin & all of Texas.",
      },
      { name: "geo.placename", content: "San Antonio, Austin, Seguin, Texas" },
      { name: "location", content: "San Antonio, Austin, Texas" },
      { property: "og:title", content: "Awning & Shade FAQ — Texas Made Shade & Covers" },
      {
        property: "og:description",
        content:
          "30+ years of answers on custom awnings, sail shades, covers, fabric care, pricing & lead times — San Antonio, Austin & across Texas.",
      },
      { property: "og:image", content: OG },
      { property: "og:image:secure_url", content: OG },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG },
    ],
  }),
  component: FaqsPage,
});
